import { Controller } from "@hotwired/stimulus";
import { Chart, registerables } from "chart.js";

// Chart.jsの全機能を登録
Chart.register(...registerables);

export class HarvestChartController extends Controller {
  static values = {
    data: Object,
    label: { type: String, default: "収穫金" },
  };

  connect() {
    this.animated = false; // スクロール時のアニメーション実行済みフラグ
    // グラフ領域のpx幅で判定（タブレット横並び時にも対応）
    this.isCompact = this.element.offsetWidth <= 600;
    this.prepareData();
    this.initChart();
    this.observeVisibility();
    this.observeResize();
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy();
    }
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  // データの準備（compact時は4ヶ月ごとにグループ化）
  prepareData() {
    const chartData = this.isCompact
      ? this.processDataForCompact(this.dataValue)
      : this.dataValue;

    this.chartLabels = chartData.labels;
    this.tooltipValues = chartData.values;
    this.barHeightValues = this.ensureMinBarHeight(chartData.values);
  }

  // Chart.jsインスタンスの生成（初期データは全0、animateChartで実データに更新）
  initChart() {
    const ctx = this.element.getContext("2d");

    this.chart = new Chart(ctx, {
      type: "bar",
      data: {
        labels: this.chartLabels,
        datasets: [
          {
            label: this.labelValue,
            data: new Array(this.barHeightValues.length).fill(0), // 初期値は0に設定してanimateChart実行時に更新
            backgroundColor: "#F4C430",
            borderRadius: 8,
            barThickness: 40,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 1500, // 1.5秒かけてアニメーション
          easing: "easeOutQuart", // イージング（最初速く、後でゆっくり）
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            padding: 12,
            titleFont: {
              size: 14,
            },
            bodyFont: {
              size: 16,
              weight: "bold",
            },
            displayColors: false,
            callbacks: {
              label: (context) => {
                const value = this.tooltipValues[context.dataIndex];
                if (value === null) return "";
                return value.toLocaleString() + "円";
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                return value.toLocaleString();
              },
            },
            grid: {
              color: "rgba(0, 0, 0, 0.05)",
            },
          },
          x: {
            grid: {
              display: false,
            },
          },
        },
      },
    });
  }

  // グラフ幅が狭い場合、12ヶ月を4つの四半期にまとめる
  // 累計値なので各四半期の最後の非null値を取れば、その四半期末時点の累計になる
  // 未到来月はnullのため、四半期内で最後の非null値を採用する
  processDataForCompact(chartData) {
    const quarters = [
      { label: "1-3月", startIndex: 0, endIndex: 2 },
      { label: "4-6月", startIndex: 3, endIndex: 5 },
      { label: "7-9月", startIndex: 6, endIndex: 8 },
      { label: "10-12月", startIndex: 9, endIndex: 11 },
    ];

    const labels = quarters.map((q) => q.label);
    const values = quarters.map((q) => {
      // 四半期内の最後の非null値を使用、全てnullならnull
      for (let i = q.endIndex; i >= q.startIndex; i--) {
        if (chartData.values[i] !== null) return chartData.values[i];
      }
      return null;
    });
    return { labels, values };
  }

  // 極端に小さい値の棒が見えなくなるのを防ぐ
  //   例: 最大値500,000円に対して100円だと棒の高さが0.02%となり視認不可能になるため、
  //   最大値の3%を最低の棒の高さとして保証（ツールチップには元の値を表示）
  ensureMinBarHeight(values) {
    const numericValues = values.filter((v) => v !== null);
    const maxValue = numericValues.length > 0 ? Math.max(...numericValues) : 0;

    // 全データが0またはnullの場合は調整不要
    if (maxValue === 0) return values;

    // 棒の最低保証高さを最大値の3%に設定
    const minBarHeight = maxValue * 0.03;

    // null値はそのまま維持（棒グラフ非表示）、0より大きいが最低保証高さに満たない値を底上げ
    return values.map((value) => {
      if (value === null) return null;
      if (0 < value && value < minBarHeight) {
        return minBarHeight;
      }
      return value;
    });
  }

  // Intersection Observerで画面に入ったことを検知
  observeVisibility() {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.5, // 50%見えたらトリガー
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.animated) {
          this.animateChart();
          this.animated = true;
          // 一度アニメーションしたら監視を停止
          this.observer.disconnect();
        }
      });
    }, options);

    // canvas要素を直接監視すると横スクロールの影響を受けるため、親階層のセクション全体を監視
    const section = this.element.closest(".mypage__harvest-chart-section");
    if (section) {
      this.observer.observe(section);
    } else {
      // セクションが見つからない場合は親要素を監視
      this.observer.observe(this.element.parentElement);
    }
  }

  // グラフをアニメーション付きで表示
  animateChart() {
    this.chart.data.datasets[0].data = this.barHeightValues;
    this.chart.update("active");
  }

  // グラフ領域のリサイズを監視
  observeResize() {
    this.resizeObserver = new ResizeObserver(() => {
      const wasCompact = this.isCompact;
      this.isCompact = this.element.offsetWidth <= 600;

      // compact ⇔ 通常 の切り替わりがあった場合のみ再描画
      if (wasCompact !== this.isCompact) {
        this.chart.destroy();
        this.animated = false;
        this.prepareData();
        this.initChart();
        this.observeVisibility();
      }
    });

    this.resizeObserver.observe(this.element);
  }
}
